import React, { useState, useEffect } from 'react';
import { Modal, Tabs, Card, Statistic, Row, Col, Alert, Spin, InputNumber, Form } from 'antd';
import { DollarOutlined, CreditCardOutlined, WalletOutlined, ScheduleOutlined } from '@ant-design/icons';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { paymentService, PaymentSummary } from '../../services/paymentService';
import StripePaymentForm from './StripePaymentForm';
import ManualPaymentForm from './ManualPaymentForm';
import PaymentPlanForm from './PaymentPlanForm';
import './PaymentModal.css';

interface PaymentModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  entityType: 'estimate' | 'work_order';
  entityId: number;
  customerId: number;
  customerEmail: string;
  customerName: string;
  totalAmount: number;
  currentSummary?: PaymentSummary;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  visible,
  onClose,
  onSuccess,
  entityType,
  entityId,
  customerId,
  customerEmail,
  customerName,
  totalAmount,
  currentSummary
}) => {
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [availableMethods, setAvailableMethods] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<PaymentSummary | undefined>(currentSummary);
  const [activeTab, setActiveTab] = useState('card');
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [affirmClientSecret, setAffirmClientSecret] = useState<string | null>(null);
  const [creatingIntent, setCreatingIntent] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState<number>(0);

  useEffect(() => {
    if (visible) {
      loadPaymentMethods();
      loadPaymentSummary();
    } else {
      // Reset client secrets when modal closes
      setClientSecret(null);
      setAffirmClientSecret(null);
    }
  }, [visible]);

  // Create payment intent when tab changes, modal opens, or payment amount changes
  useEffect(() => {
    if (visible && availableMethods.length > 0 && paymentAmount > 0) {
      // Reset client secrets when amount changes to force re-creation
      if (activeTab === 'card') {
        setClientSecret(null);
        createCardPaymentIntent(paymentAmount);
      } else if (activeTab === 'affirm') {
        setAffirmClientSecret(null);
        createAffirmPaymentIntent(paymentAmount);
      }
    }
  }, [visible, activeTab, availableMethods, paymentAmount]);

  const loadPaymentMethods = async () => {
    try {
      const methods = await paymentService.getAvailablePaymentMethods();
      setAvailableMethods(methods);

      // Load Stripe if card payments are enabled
      if (methods.includes('card')) {
        const publishableKey = await paymentService.getStripePublishableKey();
        setStripePromise(loadStripe(publishableKey));
      }

      // Set default active tab based on available methods
      if (methods.includes('card')) {
        setActiveTab('card');
      } else if (methods.includes('cash')) {
        setActiveTab('cash');
      } else if (methods.length > 0) {
        setActiveTab(methods[0]);
      }
    } catch (error) {
      console.error('Error loading payment methods:', error);
    }
  };

  const loadPaymentSummary = async () => {
    try {
      const result = await paymentService.getPaymentsForEntity(entityType, entityId);
      setSummary(result.summary);
      // Set initial payment amount to balance due
      const initialBalance = result.summary?.balance_due ?? totalAmount;
      setPaymentAmount(initialBalance);
    } catch (error) {
      console.error('Error loading payment summary:', error);
    }
  };

  const createCardPaymentIntent = async (amount: number) => {
    try {
      setCreatingIntent(true);
      const result = await paymentService.createPaymentIntent({
        amount,
        entityType,
        entityId,
        customerId,
        customerEmail,
        description: `${entityType === 'estimate' ? 'Estimate' : 'Work Order'} #${entityId}`
      });
      setClientSecret(result.clientSecret);
    } catch (error: any) {
      console.error('Error creating payment intent:', error);
    } finally {
      setCreatingIntent(false);
    }
  };

  const createAffirmPaymentIntent = async (amount: number) => {
    try {
      setCreatingIntent(true);
      const result = await paymentService.createAffirmPaymentIntent({
        amount,
        entityType,
        entityId,
        customerId,
        customerEmail,
        customerName,
        description: `${entityType === 'estimate' ? 'Estimate' : 'Work Order'} #${entityId}`
      });
      setAffirmClientSecret(result.clientSecret);
    } catch (error: any) {
      console.error('Error creating Affirm payment intent:', error);
    } finally {
      setCreatingIntent(false);
    }
  };

  const handleSuccess = () => {
    onSuccess();
    onClose();
  };

  const balanceDue = summary?.balance_due ?? totalAmount;
  const amountPaid = summary?.amount_paid ?? 0;

  const handleAmountChange = (value: number | null) => {
    if (value !== null && value > 0) {
      // Don't allow payment amount to exceed balance due
      const maxAmount = Math.min(value, balanceDue);
      setPaymentAmount(maxAmount);
    }
  };

  const tabItems = [];

  // Card Payment Tab
  if (availableMethods.includes('card')) {
    tabItems.push({
      key: 'card',
      label: (
        <span>
          <CreditCardOutlined /> Card
        </span>
      ),
      children: stripePromise && clientSecret ? (
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <StripePaymentForm
            amount={paymentAmount}
            onSuccess={handleSuccess}
          />
        </Elements>
      ) : (
        <Spin tip={creatingIntent ? "Initializing payment..." : "Loading payment form..."} />
      )
    });
  }

  // Affirm Payment Tab
  if (availableMethods.includes('affirm') && balanceDue >= 50) {
    tabItems.push({
      key: 'affirm',
      label: (
        <span>
          <WalletOutlined /> Affirm (Financing)
        </span>
      ),
      children: stripePromise && affirmClientSecret ? (
        <Elements stripe={stripePromise} options={{ clientSecret: affirmClientSecret }}>
          <StripePaymentForm
            amount={paymentAmount}
            useAffirm={true}
            onSuccess={handleSuccess}
          />
        </Elements>
      ) : (
        <Spin tip={creatingIntent ? "Initializing Affirm..." : "Loading Affirm..."} />
      )
    });
  }

  // Cash/Check Payment Tab (Manual)
  if (availableMethods.includes('cash') || availableMethods.includes('check')) {
    tabItems.push({
      key: 'manual',
      label: (
        <span>
          <DollarOutlined /> Cash/Check
        </span>
      ),
      children: (
        <ManualPaymentForm
          amount={paymentAmount}
          entityType={entityType}
          entityId={entityId}
          customerId={customerId}
          availableMethods={availableMethods}
          onSuccess={handleSuccess}
        />
      )
    });
  }

  // Payment Plan Tab
  if (availableMethods.includes('card')) {
    tabItems.push({
      key: 'plan',
      label: (
        <span>
          <ScheduleOutlined /> Payment Plan
        </span>
      ),
      children: (
        <PaymentPlanForm
          totalAmount={paymentAmount}
          entityType={entityType}
          entityId={entityId}
          customerId={customerId}
          onSuccess={handleSuccess}
        />
      )
    });
  }

  return (
    <Modal
      title="Accept Payment"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={700}
      centered
      className="payment-modal"
    >
      <div style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col span={8}>
            <Card>
              <Statistic
                title="Total Amount"
                value={totalAmount}
                precision={2}
                prefix="$"
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Amount Paid"
                value={amountPaid}
                precision={2}
                prefix="$"
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Balance Due"
                value={balanceDue}
                precision={2}
                prefix="$"
                valueStyle={{ color: balanceDue > 0 ? '#cf1322' : '#3f8600' }}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {balanceDue <= 0 ? (
        <Alert
          message="Fully Paid"
          description="This invoice has been fully paid."
          type="success"
          showIcon
        />
      ) : (
        <>
          {/* Payment Amount Input */}
          <Card style={{ marginBottom: 16 }} size="small">
            <Form layout="inline" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Form.Item label="Payment Amount" style={{ marginBottom: 0, flex: 1 }}>
                <InputNumber
                  value={paymentAmount}
                  onChange={handleAmountChange}
                  min={0.01}
                  max={balanceDue}
                  precision={2}
                  prefix="$"
                  style={{ width: '100%' }}
                  size="large"
                />
              </Form.Item>
              <div style={{ color: '#8c8c8c', fontSize: '12px' }}>
                Max: ${balanceDue.toFixed(2)}
              </div>
            </Form>
            <Alert
              message="Split Payment Enabled"
              description="You can enter any amount up to the balance due. The remaining balance can be paid later."
              type="info"
              showIcon
              style={{ marginTop: 12 }}
            />
          </Card>

          {availableMethods.length === 0 ? (
            <Alert
              message="No Payment Methods Available"
              description="Please contact the administrator to configure payment methods."
              type="warning"
              showIcon
            />
          ) : (
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={tabItems}
            />
          )}
        </>
      )}
    </Modal>
  );
};

export default PaymentModal;
